'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { Button } from '@udemy-v2/react-core-components';
import { Loader } from '@udemy-v2/react-reveal-components';
import { ClpPrimaryCtaButton } from '@/features/course-landing-page/components/clp-primary-cta-button/clp-primary-cta-button.react-component';
import { SubscriptionStickyFooterCta } from '@/features/course-landing-page/components/sticky-footer/subscription-sticky-footer-content/subscription-sticky-footer-cta.react-component';
import { NonCatalogPPStickyFooterCta } from '@/features/course-landing-page/components/sticky-footer/non-catalog-pp-sticky-footer-content/non-catalog-pp-sticky-footer-cta.react-component';
import { TransactionalStickyFooterContent } from '@/features/course-landing-page/components/sticky-footer/transactional-sticky-footer-content/transactional-sticky-footer-content.react-component';
import { EnrolledStickyFooterContent } from '@/features/course-landing-page/components/sticky-footer/enrolled-sticky-footer-content/enrolled-sticky-footer-content.react-component';
import { EnrollmentStickyFooterContent } from '@/features/course-landing-page/components/sticky-footer/enrollment-sticky-footer-content/enrollment-sticky-footer-content.react-component';
import { GrowWithGoogleStickyFooterContent } from '@/features/course-landing-page/components/sticky-footer/grow-with-google-sticky-footer-content/grow-with-google-sticky-footer-content.react-component';
import { OrganizationStickyFooterContent } from '@/features/course-landing-page/components/sticky-footer/organization-sticky-footer-content/organization-sticky-footer-content.react-component';
import { CLPPurchaseOption, useClpPurchaseContext, useClpSelectedOption } from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import { useUserHasOrganization } from '@/lib/hooks/use-user-has-organization';
import styles from './sticky-footer-content.module.scss';

/**
 * StickyFooterContent handles conditional rendering of different sticky footer content
 * based on course enrollment status, subscription availability, organization membership,
 * and loading state.
 */
export const StickyFooterContent: React.FC = () => {
  const $ = _c(9);
  const {
    isCourseInAvailablePlans,
    isLoading,
    isEnrolledInCourse,
    isFree,
    isCourseIncludedInUserSubscription,
    isCourseIncludedInGrowWithGoogleSubscription,
    isEligibleForNonCatalogPPOffer,
    isConsumerSubscriptionPaused
  } = useClpPurchaseContext();
  const {
    selectedPurchaseOption
  } = useClpSelectedOption();
  const hasOrganization = useUserHasOrganization();
  if (isLoading) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <Button className={styles["loading-button"]} disabled={true}><Loader color="inherit" overlay={true} /></Button>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (isConsumerSubscriptionPaused) {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <ClpPrimaryCtaButton udStyle="primary" />;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (hasOrganization) {
    let t0;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <OrganizationStickyFooterContent />;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  }
  if (isCourseIncludedInGrowWithGoogleSubscription) {
    let t0;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <GrowWithGoogleStickyFooterContent />;
      $[3] = t0;
    } else {
      t0 = $[3];
    }
    return t0;
  }
  if (isCourseIncludedInUserSubscription || isFree && !isEnrolledInCourse) {
    let t0;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <EnrollmentStickyFooterContent />;
      $[4] = t0;
    } else {
      t0 = $[4];
    }
    return t0;
  }
  if (isEnrolledInCourse) {
    let t0;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <EnrolledStickyFooterContent />;
      $[5] = t0;
    } else {
      t0 = $[5];
    }
    return t0;
  }
  if (isEligibleForNonCatalogPPOffer && selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION) {
    let t0;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <NonCatalogPPStickyFooterCta />;
      $[6] = t0;
    } else {
      t0 = $[6];
    }
    return t0;
  }
  if (isCourseInAvailablePlans && selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION) {
    let t0;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <SubscriptionStickyFooterCta />;
      $[7] = t0;
    } else {
      t0 = $[7];
    }
    return t0;
  }
  let t0;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <TransactionalStickyFooterContent />;
    $[8] = t0;
  } else {
    t0 = $[8];
  }
  return t0;
};